
import { useState, useEffect } from "react";
import { Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { NotificationItem } from "./NotificationItem";
import { useNotifications } from "@/hooks/use-notifications";
import { supabase } from "@/integrations/supabase/client";

export const NotificationsPopover = () => {
  const [userId, setUserId] = useState<string | undefined>();
  
  useEffect(() => {
    const fetchUser = async () => {
      const { data } = await supabase.auth.getSession();
      if (data.session?.user) {
        setUserId(data.session.user.id);
      }
    };
    
    fetchUser();
  }, []);
  
  const { 
    notifications, 
    unreadCount, 
    markAllAsRead,
    clearAllNotifications,
    createWelcomeNotification
  } = useNotifications(userId);
  
  const [open, setOpen] = useState(false);
  
  // Check if user is newly onboarded and create welcome notification
  useEffect(() => {
    if (userId) {
      const checkAndCreateWelcome = async () => {
        try {
          const { data } = await supabase
            .from('profiles')
            .select('is_onboarded')
            .eq('id', userId)
            .single();
            
          // Only create welcome notification if user is onboarded
          if (data?.is_onboarded) {
            await createWelcomeNotification();
          }
        } catch (error) {
          console.error("Error checking onboarding status:", error);
        }
      };
      
      checkAndCreateWelcome();
    }
  }, [userId, createWelcomeNotification]);
  
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-[10px] text-primary-foreground">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0">
        <div className="flex items-center justify-between border-b border-border p-3">
          <h3 className="font-medium">Notificações</h3>
          <div className="flex gap-1">
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-8 text-xs px-2"
              onClick={markAllAsRead}
            >
              Marcar todas como lidas
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-8 text-xs px-2"
              onClick={clearAllNotifications}
            >
              Limpar todas
            </Button>
          </div>
        </div>
        <ScrollArea className="h-[calc(80vh-80px)] max-h-[400px]">
          {notifications.length === 0 ? (
            <div className="flex h-20 items-center justify-center text-sm text-muted-foreground">
              Nenhuma notificação
            </div>
          ) : (
            <div className="flex flex-col">
              {notifications.map((notification) => (
                <NotificationItem 
                  key={notification.id} 
                  notification={notification} 
                  compact
                />
              ))}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
};
